import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { FILTER_AXES, MAX_TERMS } from '../../shared/queries.ts';
import type { FilterTerm, IssueFilter, SearchAxis } from '../../shared/queries.ts';
import { toast } from '../api.ts';
import { parseTerm, PROMPT, suggest, termLabel, withTerm } from '../search.ts';
import type { Option } from '../search.ts';
import type { User } from '../types.ts';

/**
 * The faceted search: a two-stage combobox over the axes, then over that axis's values.
 *
 * Each term is `axis:value` — or bare text, which searches titles — and the terms are ANDed (the
 * values of ONE equality axis are ORed; `queries.ts`'s `issueConditions` has the why). They are
 * not applied in the page: they become part of the live query, so r2db maintains an
 * already-filtered window.
 *
 * **Two stages, because the axis and the value are different questions.** Typing narrows the six
 * axes; choosing one puts it in the box as an open pill and the SAME input now narrows that axis's
 * values — the cast for `owner:`, the tag vocabulary for `tag:`, five statuses, four priorities.
 * `title:` and `comment:` have no vocabulary, so their one row is the text you typed. The list is
 * reachable by arrow keys and by mouse, and the free-typed `status:review` path still works: a
 * recognized `axis:` prefix moves the draft into stage two as if it had been chosen.
 *
 * **Every axis that can be exact is resolved here, not sent as a substring.** `status` and
 * `priority` always were; `owner` and `tag` are too, which is what the value list is for. The
 * reason is a cost one and it belongs in this file because this is where the value is chosen: an
 * `ILIKE '%li%'` seeks no index prefix and matches every row it reads, so an owner axis spelled as
 * a substring is a comparison the engine must make against every candidate row and against every
 * row of the child table if the planner flips the join. Resolved to `li` it is an exact comparison
 * against one value. The user is typing a name either way; the difference is whether the page
 * hands the engine something it can use.
 */
export function Search({ filter, users, onChange }: { filter: IssueFilter; users: User[]; onChange: (next: IssueFilter) => void }) {
  const [axis, setAxis] = useState<SearchAxis | null>(null);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const options = useMemo(() => suggest(axis, draft, users), [axis, draft, users]);
  // A new draft or a new stage starts at the top of the list rather than wherever the last one
  // left the cursor, which would otherwise highlight a row the typing did not choose.
  useEffect(() => setActive(0), [axis, draft]);
  useEffect(() => { if (open) listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [active, open]);

  const reset = () => { setAxis(null); setDraft(''); setActive(0); };
  const add = (term: FilterTerm) => {
    const next = withTerm(filter, term);
    if (!next) { toast(`${MAX_TERMS} terms is the most one search takes.`); return; }
    onChange(next);
    reset();
    inputRef.current?.focus();
  };
  const choose = (option: Option) => {
    if (option.kind === 'axis') {
      setAxis(option.axis);
      setDraft('');
      setOpen(true);
      inputRef.current?.focus();
      return;
    }
    add({ axis: option.axis, value: option.value });
  };
  /** Enter: take the highlighted row, or — with nothing to highlight — whatever was typed. */
  const commit = () => {
    const option = options[active];
    if (option) { choose(option); return; }
    const parsed = parseTerm(axis ? `${axis}:${draft}` : draft, users);
    if (!parsed) return;
    if ('refusal' in parsed) { toast(parsed.refusal); return; }
    add(parsed.term);
  };
  // A typed `owner:` is the same thing as picking `owner` from the list, so it moves into stage
  // two rather than being parsed later. This is what keeps the advertised syntax working now that
  // the list is the main way in.
  const type = (value: string) => {
    setOpen(true);
    if (!axis) {
      const colon = value.indexOf(':');
      const typed = colon > 0 ? value.slice(0, colon).trim().toLowerCase() : '';
      if (FILTER_AXES.includes(typed as SearchAxis)) { setAxis(typed as SearchAxis); setDraft(value.slice(colon + 1).trimStart()); return; }
    }
    setDraft(value);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (options.length) setActive((active + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      return;
    }
    if (e.key === 'Enter' || (e.key === 'Tab' && open && options.length)) { e.preventDefault(); commit(); return; }
    // Escape closes the list; with it closed, it backs out of the axis — so the key always undoes
    // one step rather than everything, and the control is still escapable by keyboard.
    if (e.key === 'Escape') { if (open) setOpen(false); else reset(); return; }
    if (e.key === 'Backspace' && !draft) {
      if (axis) setAxis(null);
      else if (filter.length) onChange(filter.slice(0, -1));
    }
  };

  return (
    <div className="search">
      {/* The box itself is the click target: the pills and the input sit in it, so a click
          anywhere lands in the input, which is where typing goes. */}
      <div className="search-box" onMouseDown={e => { if (e.target === e.currentTarget) { e.preventDefault(); inputRef.current?.focus(); setOpen(true); } }}>
        {filter.map((term, i) => (
          <span key={`${term.axis}:${term.value}`} className="token">
            <span className="micro">{term.axis}</span>{termLabel(term, users)}
            <button type="button" aria-label={`Remove ${term.axis} ${term.value}`} onClick={() => onChange(filter.filter((_, j) => j !== i))}>×</button>
          </span>
        ))}
        {/* The axis, chosen but not yet valued: the same pill, open at the end. */}
        {axis && <span className="token pending"><span className="micro">{axis}</span></span>}
        <input
          ref={inputRef} value={draft} aria-label="Search issues"
          role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
          placeholder={axis ? PROMPT[axis] : filter.length ? 'Add another term…' : 'Search titles, or pick a facet…'}
          onChange={e => type(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && options.length > 0 && (
        <ul className="suggest" role="listbox" id={listId} ref={listRef} aria-label={axis ? `${axis} values` : 'Search by'}>
          {options.map((option, i) => (
            <li
              key={option.kind === 'axis' ? option.axis : `${option.axis}:${option.value}`}
              id={`${listId}-${i}`} role="option" aria-selected={i === active}
              // `mousemove`, not `mouseenter`: the list is replaced under a resting pointer every
              // time a term is committed, and `mouseenter` fires on that, so the highlight would
              // jump to wherever the mouse happened to be left after a KEYBOARD choice.
              onMouseMove={() => setActive(i)}
              // The input must still have focus when the click lands, or the box closes first and
              // the click hits nothing.
              onMouseDown={e => e.preventDefault()}
              onClick={() => choose(option)}
            >
              <span className="suggest-label">{option.label}</span>
              <span className="micro muted">{option.hint}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="axes" aria-label="Search by">
        {FILTER_AXES.map(a => (
          <button key={a} type="button" className={`axis${a === axis ? ' current' : ''}`} onClick={() => { setAxis(a); setDraft(''); setOpen(true); inputRef.current?.focus(); }}>{a}:</button>
        ))}
        {filter.length > 0 && <button type="button" className="axis clear" onClick={() => { onChange([]); reset(); }}>clear</button>}
      </div>
    </div>
  );
}
